import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { resetStores } from '@datorama/akita';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { catchError, filter, finalize, map, of, switchMap, take, tap, throwError } from 'rxjs';

import { AuthCacheGuardService } from '@auth/service/auth-cache-guard.service';
import { UserQuery, UserState } from '@common/store/user.store';
import { UserProfile } from '@common/types';
import {
  STORAGE_KEY_ENTITY_ID,
  STORAGE_KEY_IS_LOGGED_IN,
  STORAGE_KEY_USER_ID,
  URL_FRAGMENT_REDIRECT_URL,
} from '@common/util/constants';
import { getNestedValue, isEmpty } from '@common/util/util';

import { AuthDataService } from './auth-data.service';
import { AuthSessionService } from './auth-session.service';
import { DomainService } from './domain.service';
import { FeatureFlagService } from './feature-flag.service';
import { LocalStorageService } from './localstorage.service';
import { SystemNotificationService } from './system-notification.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(
    private oidcSecurityService: OidcSecurityService,
    private userStore: UserState,
    private userQuery: UserQuery,
    private httpClient: HttpClient,
    private router: Router,
    private domainService: DomainService,
    private systemNotification: SystemNotificationService,
    private localstorageService: LocalStorageService,
    private authDataService: AuthDataService,
    private authCacheGuardService: AuthCacheGuardService,
    private featureFlagService: FeatureFlagService,
    private authSessionService: AuthSessionService,
  ) {}

  authWatcher$ = this.authDataService.getAuthData().pipe(
    filter((authData) => !isEmpty(authData)),
    map((authData) => authData.client_id),
    switchMap((clientId) => this.localstorageService.watch$(`0-${clientId}`)),
  );

  accessToken$ = this.authWatcher$.pipe(
    map((oidcStorageData) => {
      const oidcData = oidcStorageData as Record<string, object>;
      const tokenData = {
        access_token_expires_at: getNestedValue(oidcData, 'access_token_expires_at') as number,
        access_token: getNestedValue(oidcData, 'authnResult.access_token') as string,
      };

      return tokenData;
    }),
  );

  /* isUserAuthenticated$ = this.oidcSecurityService.isAuthenticated$.pipe(
    map((authInfo) => {
      return authInfo.isAuthenticated;
    }),
  ); */

  /**
   * Today's OIDC-derived auth state: valid, unexpired access token in the
   * `0-${clientId}` localStorage key. Preserved verbatim (incl. the
   * setLoginStatus side-effect) as the fallback source.
   */
  private oidcIsUserAuthenticated$ = this.accessToken$.pipe(
    map((tokenData) => this.validateAccessToken(tokenData)),
    tap((isAuthenticated) => {
      if (!isAuthenticated) {
        this.setLoginStatus(false);
      }
    }),
  );

  /**
   * Public auth-state observable — API unchanged (Observable<boolean>).
   *
   * AK-476: behind the cookie-session migration toggle, auth state is the
   * API-owned cookie session ONLY — no OIDC consultation (under broker mode there
   * is no JWT to validate). With the toggle OFF (the default) this is exactly
   * today's OIDC behaviour.
   */
  isUserAuthenticated$ = this.featureFlagService.cookieSessionMigrationEnabled$.pipe(
    switchMap((enabled) => {
      if (!enabled) {
        return this.oidcIsUserAuthenticated$;
      }
      return this.authSessionService.session$.pipe(map((session) => session.authenticated));
    }),
  );

  private userProfile$ = this.userQuery.user$;
  private _perceivedAuthenticationState = false;

  get perceivedAuthenticationState() {
    return this._perceivedAuthenticationState;
  }

  authUser(queryParams?: { [key: string]: string }) {
    return this.authCacheGuardService.invalidateIfStale().pipe(
      catchError((err) => {
        console.warn('[AuthCacheGuard] Failed to check cache staleness — proceeding anyway', err);
        return of(undefined as void);
      }),
      switchMap(() => {
        const { state, code } = queryParams || {};

        if (state && code) {
          return of(null);
        } else {
          // AK-476: only attempt forceRefreshSession when a refresh token actually
          // exists. For a guest (none) it always fails, AND the library's failure
          // cascade calls resetStorageFlowData() — which wipes the PKCE
          // `codeVerifier` a concurrent loginGuard authorize() has just written,
          // breaking the subsequent /oauth2/token exchange (empty body -> 400, the
          // JWT new-login regression). This race surfaced once bootstrap auth was
          // reordered to run after the domain fetch (so the doomed refresh now lands
          // after authorize() instead of before it). When there is no refresh token,
          // reproduce the prior unauthenticated outcome WITHOUT touching OIDC flow
          // storage, so the login flow's code_verifier survives.
          return this.hasStoredRefreshToken().pipe(
            switchMap((hasRefreshToken) => {
              if (!hasRefreshToken) {
                this.setLoginStatus(false);
                this._perceivedAuthenticationState = false;
                return throwError(() => new Error('no refresh token; user is unauthenticated'));
              }
              return this.oidcSecurityService.forceRefreshSession().pipe(
                catchError((error) => {
                  this.setLoginStatus(false);
                  this._perceivedAuthenticationState = false;
                  console.error('error in session refresh', error);
                  throw error;
                }),
              );
            }),
          );
        }
      }),
      switchMap(() => {
        return this.oidcSecurityService.checkAuth().pipe(
          take(1),
          tap((loginResponse) => {
            if (!isEmpty(loginResponse) && loginResponse.isAuthenticated) {
              this._perceivedAuthenticationState = true;
              this.setLoginStatus(true);
            }
          }),
          switchMap(() => this.getUserInfo()),
          catchError((error) => {
            console.error('CheckAuth Error:', error);
            throw error;
          }),
        );
      }),
    );
  }

  /**
   * AK-476: whether a usable OIDC refresh token is persisted. Reads it straight
   * from the `0-${clientId}` blob (not `oidcSecurityService.getRefreshToken()`,
   * which returns '' unless the access token is still valid) so an
   * expired-but-refreshable returning session still counts as refreshable.
   */
  private hasStoredRefreshToken() {
    return this.authDataService.getAuthData().pipe(
      filter((authData) => !isEmpty(authData)),
      take(1),
      map((authData) => {
        const blob = this.localstorageService.get<Record<string, unknown>>(`0-${authData.client_id}`);
        if (!blob || typeof blob !== 'object') {
          return false;
        }
        return !!getNestedValue(blob, 'authnResult.refresh_token');
      }),
    );
  }

  login(redirectUrl?: { pathname: string; hash?: string }) {
    // AK-476 (WP-F5): in cookie-session mode the server owns the Cognito dance.
    // We navigate to the relative /api/auth/login/start endpoint and let the
    // server drive the redirect (see startCookieSessionLogin); OIDC `authorize()`
    // below stays as the toggle-OFF fallback.
    if (this.featureFlagService.cookieSessionMigrationEnabled) {
      this.startCookieSessionLogin(redirectUrl);
      return;
    }

    // const domainData = this.domainState.getValue();

    this.authDataService
      .getAuthData()
      .pipe(
        filter((authData) => !isEmpty(authData)),
        switchMap((authData) => {
          const preauthUri = authData.preauth_uri;
          const targetUrl = new URL(`${window.location.origin}/auth`);
          const { pathname, hash } = redirectUrl?.pathname ? redirectUrl : window.location;
          targetUrl.searchParams.set(URL_FRAGMENT_REDIRECT_URL, `${pathname}${hash || ''}`);

          return this.oidcSecurityService.getConfiguration().pipe(
            take(1),
            tap((config) => {
              if (config?.configId) {
                this.systemNotification.showSpinner('Redirecting to login...');

                this.oidcSecurityService.authorize(config.configId, {
                  urlHandler: (url) => {
                    const configId = config.configId;
                    const existingConfig = localStorage.getItem(configId!);
                    const configJson = JSON.parse(existingConfig!);

                    if (existingConfig) {
                      const state = configJson['authStateControl'];

                      this.httpClient
                        .post(preauthUri, {
                          state,
                          target: targetUrl.toString(),
                        })
                        .pipe(
                          take(1),
                          tap(() => {
                            window.location.href = url;
                          }),
                          catchError((error) => {
                            console.error('Preauth Error:', error);
                            return of(null);
                          }),
                        )
                        .subscribe();
                    }
                  },
                });
              }
            }),
          );
        }),
      )
      .subscribe();
  }

  /**
   * Cookie-session login (AK-476 WP-F5), revised AK-474 contract — relative-nav +
   * Referer (2026-06-30):
   *   The browser does a full-page navigation to the RELATIVE endpoint
   *   `/api/auth/login/start?target=<relative-path>`. The server reads the
   *   return origin (protocol/host/port) from the request `Referer` header
   *   (whitelist-validated), runs the Cognito → /auth/postauth Set-Cookie redirect
   *   chain, and 302s back to `<origin>/auth?sessionId=…&redirectUrl=<target>`.
   *
   * Two deliberate choices:
   * - `target` is a RELATIVE path (+ fragment), never an absolute URL. An absolute
   *   URL would carry `localhost` in a query arg and trip the WAF SSRF rule
   *   (EC2MetaDataSSRF_QUERYARGUMENTS — see AK-537); the origin is supplied by the
   *   Referer regardless. The fragment is carried in `target` because the Referer
   *   header strips fragments (so chapter anchors survive the round trip).
   * - This MUST be a full-page navigation, not an HttpClient call: only a top-level
   *   GET follows the Set-Cookie redirect chain, and the relative `/api` path is
   *   served same-origin (in dev the dev-server proxy forwards it). It therefore
   *   bypasses the interceptor — no `api.core` rewrite, no `mode`/withCredentials.
   */
  private startCookieSessionLogin(redirectUrl?: { pathname: string; hash?: string }) {
    const path = redirectUrl?.pathname
      ? `${redirectUrl.pathname}${redirectUrl.hash || ''}`
      : `${window.location.pathname}${window.location.hash}`;
    const target = path.startsWith('/') ? path : `/${path}`;

    this.systemNotification.showSpinner('Redirecting to login...');
    this.redirectTo(`/api/auth/login/start?target=${encodeURIComponent(target)}`);
  }

  /** Full-page navigation seam — spied in tests so the runner doesn't navigate. */
  private redirectTo(url: string): void {
    window.location.href = url;
  }

  logout(type: 'session' | 'global' = 'global') {
    this._perceivedAuthenticationState = false;
    this.setLoginStatus(false);

    // AK-476 (WP-F5): cookie-session logout is a single POST — the server
    // revokes the SessionRecord and expires `aiwb_session` + CloudFront cookies.
    // No OIDC refresh token to surrender.
    if (this.featureFlagService.cookieSessionMigrationEnabled) {
      this.cookieSessionLogout(type);
      return;
    }

    this.oidcSecurityService
      .getRefreshToken()
      .pipe(
        take(1),
        switchMap((refreshToken) => {
          const body: { type: 'session' | 'global'; refresh_token?: string } = {
            type,
          };

          if (type === 'session') {
            if (refreshToken) {
              body.refresh_token = refreshToken;
            }
          }

          const overlayRef = this.systemNotification.showSpinner('Logging out...');

          return this.httpClient.post('/api/auth/logout', body).pipe(
            take(1),
            tap(() => {
              // Reset all Akita stores - this will update authorisation.service observables
              resetStores();

              if (window.localStorage) {
                window.localStorage.clear();
              }
            }),
            switchMap(() => {
              return this.domainService.fetchDomainInfo().pipe(
                catchError((domainError) => {
                  console.error('Domain Info Error:', domainError);
                  return of(null);
                }),
              );
            }),
            switchMap(() => {
              // Explicitly log out from OIDC to update authentication state
              return this.oidcSecurityService.checkAuth().pipe(
                catchError((oidcError) => {
                  console.error('OIDC Logout Error:', oidcError);
                  return of(null);
                }),
              );
            }),
            catchError((error) => {
              console.error('Logout Error:', error);
              if (window.localStorage) {
                window.localStorage.clear();
              }
              return of(null);
            }),
            finalize(() => {
              overlayRef.close();
              this.router.navigate(['/']);
            }),
          );
        }),
      )
      .subscribe();
  }

  private cookieSessionLogout(type: 'session' | 'global') {
    const overlayRef = this.systemNotification.showSpinner('Logging out...');

    this.httpClient
      .post('/api/auth/logout', { type }, { withCredentials: true })
      .pipe(
        take(1),
        catchError((error) => {
          console.error('Logout Error:', error);
          return of(null);
        }),
        tap(() => {
          this.authSessionService.clear();
          // Reset all Akita stores - this will update authorisation observables
          resetStores();
          if (window.localStorage) {
            window.localStorage.clear();
          }
        }),
        switchMap(() => {
          return this.domainService.fetchDomainInfo().pipe(
            catchError((domainError) => {
              console.error('Domain Info Error:', domainError);
              return of(null);
            }),
          );
        }),
        finalize(() => {
          overlayRef.close();
          this.router.navigate(['/']);
        }),
      )
      .subscribe();
  }

  getUserInfo(refresh = false) {
    if (!isEmpty(this.userQuery.getValue()) && !refresh) {
      return this.userProfile$;
    }

    return this.httpClient.get<UserProfile>('/api/user').pipe(
      take(1),
      tap((userProfile: UserProfile) => {
        if (!isEmpty(userProfile)) {
          this.userStore.update(userProfile);
          this.localstorageService.set(STORAGE_KEY_USER_ID, userProfile.user_id);
          this.localstorageService.set(STORAGE_KEY_ENTITY_ID, userProfile.entity?.entity_id);
        }
      }),
      switchMap(() => {
        return this.userProfile$;
      }),
      catchError((error) => {
        console.error('Get User Info Error:', error);
        return of(null);
      }),
    );
  }

  updateUserInfo(userData: { first_name: string; last_name: string }) {
    this.userStore.update({
      ...(this.userQuery.getValue() ? this.userQuery.getValue() : ({} as UserProfile)),
      ...userData,
    });
  }

  getLoginStatus() {
    return this.localstorageService.get(STORAGE_KEY_IS_LOGGED_IN);
  }

  setLoginStatus(status: boolean) {
    this.localstorageService.set(STORAGE_KEY_IS_LOGGED_IN, status);
  }

  private validateAccessToken(tokenData: { access_token_expires_at: number; access_token: string }): boolean {
    if (!tokenData.access_token) {
      return false;
    }

    const now = Date.now();
    const expiresAt = tokenData.access_token_expires_at;

    return now < expiresAt;
  }
}
